import React, { useEffect, useRef } from "react";
import { SectionCard, dangerButtonStyle } from "./patientChartShared";

const alwaysOpenCardStyle = {
  border: "1px solid #ddd6fe",
  borderTop: "5px solid #7c3aed",
  borderRadius: 16,
  background: "#ffffff",
  padding: 14,
  boxShadow: "0 8px 22px rgba(15, 23, 42, 0.05)",
  display: "grid",
  gap: 16,
};

const canvasShellStyle = {
  width: "100%",
  border: "2px solid",
  borderRadius: 8,
  background: "#fff",
  overflow: "hidden",
};

const actionsRowStyle = {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  gap: 12,
  flexWrap: "wrap",
  marginTop: 8,
};

export default function SignatureSection({
  selectedEncounterId,
  signatureForm = {},
  setSignatureField,
  expandedSections,
  toggleSection,
  sectionRef,
  activeSection,
  getMissingCount,
  isComplete,
  readOnlyMode = false,
  errorCount = 0,
  alwaysOpen = false,
}) {
  const wrapRef = useRef(null);
  const canvasRef = useRef(null);
  const hasEncounter =
    selectedEncounterId !== undefined ? Boolean(selectedEncounterId) : true;

  useEffect(() => {
    const wrapper = wrapRef.current;
    const canvas = canvasRef.current;
    if (!wrapper || !canvas) return undefined;

    const ctx = canvas.getContext("2d");
    let drawing = false;

    function resizeCanvas() {
      const rect = wrapper.getBoundingClientRect();
      const ratio = window.devicePixelRatio || 1;
      const displayWidth = Math.max(300, Math.floor(rect.width));
      const displayHeight = 180;
      const previous = canvas.toDataURL();

      canvas.width = displayWidth * ratio;
      canvas.height = displayHeight * ratio;
      canvas.style.width = `${displayWidth}px`;
      canvas.style.height = `${displayHeight}px`;

      const freshCtx = canvas.getContext("2d");
      freshCtx.setTransform(1, 0, 0, 1, 0, 0);
      freshCtx.scale(ratio, ratio);
      freshCtx.lineWidth = 2;
      freshCtx.lineCap = "round";
      freshCtx.strokeStyle = "#000";

      if (signatureForm.patientSignature) {
        const img = new Image();
        img.onload = () => freshCtx.drawImage(img, 0, 0, displayWidth, displayHeight);
        img.src = signatureForm.patientSignature;
      } else if (previous && previous !== "data:,") {
        const img = new Image();
        img.onload = () => freshCtx.drawImage(img, 0, 0, displayWidth, displayHeight);
        img.src = previous;
      }
    }

    function getPoint(e) {
      const rect = canvas.getBoundingClientRect();
      const clientX = e.touches ? e.touches[0].clientX : e.clientX;
      const clientY = e.touches ? e.touches[0].clientY : e.clientY;
      return { x: clientX - rect.left, y: clientY - rect.top };
    }

    function start(e) {
      if (readOnlyMode) return;
      e.preventDefault();
      drawing = true;
      const point = getPoint(e);
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    }

    function end(e) {
      if (readOnlyMode) return;
      if (e) e.preventDefault();
      if (!drawing) return;
      drawing = false;
      ctx.beginPath();
      setSignatureField("patientSignature", canvas.toDataURL("image/png"));
    }

    function draw(e) {
      if (readOnlyMode || !drawing) return;
      e.preventDefault();
      const point = getPoint(e);
      ctx.lineTo(point.x, point.y);
      ctx.stroke();
      ctx.beginPath();
      ctx.moveTo(point.x, point.y);
    }

    resizeCanvas();

    const observer = new ResizeObserver(resizeCanvas);
    observer.observe(wrapper);

    canvas.addEventListener("mousedown", start);
    canvas.addEventListener("mouseup", end);
    canvas.addEventListener("mouseleave", end);
    canvas.addEventListener("mousemove", draw);
    canvas.addEventListener("touchstart", start, { passive: false });
    canvas.addEventListener("touchend", end, { passive: false });
    canvas.addEventListener("touchcancel", end, { passive: false });
    canvas.addEventListener("touchmove", draw, { passive: false });

    return () => {
      observer.disconnect();
      canvas.removeEventListener("mousedown", start);
      canvas.removeEventListener("mouseup", end);
      canvas.removeEventListener("mouseleave", end);
      canvas.removeEventListener("mousemove", draw);
      canvas.removeEventListener("touchstart", start);
      canvas.removeEventListener("touchend", end);
      canvas.removeEventListener("touchcancel", end);
      canvas.removeEventListener("touchmove", draw);
    };
  }, [setSignatureField, signatureForm.patientSignature, readOnlyMode]);

  function clearCanvas() {
    if (readOnlyMode) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    setSignatureField("patientSignature", "");
  }

  const missingCount =
    errorCount ||
    getMissingCount?.("signatures") ||
    getMissingCount?.("signature") ||
    0;

  const content = !hasEncounter ? (
    <div style={{ color: "#64748b" }}>Create or select an encounter first.</div>
  ) : (
    <div style={{ display: "grid", gap: 16 }}>
      <div>
        <div style={{ marginBottom: 6, fontWeight: 600 }}>Patient Signature *</div>

        {!signatureForm.unableToObtain && (
          <div
            ref={wrapRef}
            style={{
              ...canvasShellStyle,
              borderColor: signatureForm.patientSignature ? "#22c55e" : "#ef4444",
              opacity: readOnlyMode ? 0.65 : 1,
            }}
          >
            <canvas
              ref={canvasRef}
              style={{
                display: "block",
                width: "100%",
                height: 180,
                background: "#fff",
                touchAction: readOnlyMode ? "auto" : "none",
                cursor: readOnlyMode ? "not-allowed" : "crosshair",
              }}
            />
          </div>
        )}

        <div style={actionsRowStyle}>
          <button
            type="button"
            style={dangerButtonStyle}
            onClick={clearCanvas}
            disabled={readOnlyMode}
          >
            Clear
          </button>

          <label style={{ display: "flex", gap: 8, alignItems: "center" }}>
            <input
              type="checkbox"
              checked={signatureForm.unableToObtain || false}
              disabled={readOnlyMode}
              onChange={(e) => {
                if (readOnlyMode) return;
                setSignatureField("unableToObtain", e.target.checked);
                if (e.target.checked) clearCanvas();
              }}
            />
            Unable to Obtain
          </label>
        </div>
      </div>
    </div>
  );

  if (alwaysOpen) {
    return (
      <div ref={sectionRef} style={alwaysOpenCardStyle}>
        <div style={{ fontSize: 17, fontWeight: 800, color: "#6d28d9" }}>
          Signature {missingCount > 0 ? `(${missingCount})` : ""}
        </div>
        {content}
      </div>
    );
  }

  return (
    <SectionCard
      title="Signature"
      errorCount={missingCount}
      sectionKey="signatures"
      expandedSections={expandedSections}
      toggleSection={toggleSection}
      sectionRef={sectionRef}
      activeSection={activeSection}
      isComplete={isComplete?.("signatures") || isComplete?.("signature")}
    >
      {content}
    </SectionCard>
  );
}